import type { ExcalidrawElement } from "@excalidraw/element/types";
import type { BinaryFiles } from "@excalidraw/excalidraw/types";

// "Save a copy" forks the current scene into a brand-new server diagram. Because
// opening /d/<muid> does a full page navigation (losing in-memory scene) and the
// new DiagramRoom starts empty, we stash the scene here keyed by the new MUID,
// then apply it once the fresh room's SCENE_INIT arrives (see Collab INIT
// handler). sessionStorage survives the navigation and is tab-scoped.

interface CopySeed {
  elements: readonly ExcalidrawElement[];
  files: BinaryFiles;
}

const key = (muid: string) => `excalidraw-copy-seed:${muid}`;

export const stashCopySeed = (muid: string, seed: CopySeed): void => {
  try {
    sessionStorage.setItem(key(muid), JSON.stringify(seed));
  } catch {
    // Quota (large embedded images) — fall back to seeding elements only so at
    // least the shapes carry over; missing files render as placeholders.
    try {
      sessionStorage.setItem(
        key(muid),
        JSON.stringify({ elements: seed.elements, files: {} }),
      );
    } catch {
      // give up silently; the copy will just open empty
    }
  }
};

export const consumeCopySeed = (muid: string): CopySeed | null => {
  try {
    const raw = sessionStorage.getItem(key(muid));
    if (!raw) {
      return null;
    }
    sessionStorage.removeItem(key(muid));
    return JSON.parse(raw) as CopySeed;
  } catch {
    return null;
  }
};
